// 🩺 B252 (AI_114) — DECLARAÇÃO DE AGENDAMENTO (modal de preenchimento)
// ─────────────────────────────────────────────────────────────
// Documento PRÓPRIO da enfermagem. A enfermeira preenche os campos específicos
// (o sistema já traz nome/CNS/Hospub do paciente), salva no histórico e gera o
// PDF para impressão. Sem IA, sem bloco de assinatura (encerra como os laudos).
// Interface exclusiva da enfermagem (gate ehEnfermeiro na sub-aba e nas rules).

import React, { useState } from "react";
import { FileText, Printer, Check, Clock } from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import {
  gerarPDFDeclaracaoAgendamento,
  montarConfigDeclaracao,
  HOSPITAL_PADRAO,
  DadosDeclaracaoAgendamento,
} from "../services/gerarDeclaracaoAgendamento";
import {
  criarDocumentoEnfermagem,
  atualizarDocumentoEnfermagem,
  DocumentoEnfermagem,
} from "../services/documentosEnfermagem";

interface Props {
  paciente: Paciente;
  documento?: DocumentoEnfermagem | null; // edição de um já existente
  onClose: () => void;
  onSalvo?: () => void;
}

// dd/mm/aaaa ⇄ yyyy-mm-dd (parsing manual, sem Date, p/ evitar fuso horário).
function isoParaBR(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}
function brParaISO(br: string): string {
  if (!br) return "";
  const [d, m, a] = br.split("/");
  return d && m && a ? `${a}-${m}-${d}` : "";
}
// "07:00" → "07h00"  e  "07h00" → "07:00"
function horaParaBR(hhmm: string): string {
  return hhmm ? hhmm.replace(":", "h") : "";
}
function horaParaInput(v: string): string {
  return v ? v.replace("h", ":") : "";
}

export function DeclaracaoAgendamento({
  paciente,
  documento,
  onClose,
  onSalvo,
}: Props) {
  const { usuarioLogado } = useAuth();

  const d = documento?.dados;
  const [agendamento, setAgendamento] = useState(d?.agendamento || "");
  const [dataISO, setDataISO] = useState(brParaISO(d?.data || ""));
  const [hora, setHora] = useState(horaParaInput(d?.hora || ""));
  const [setor, setSetor] = useState(d?.setor || "");
  // Hospital/unidade: novo documento já vem com o HB preenchido (caso comum);
  // editável p/ programações fora do hospital. Edição usa o que foi salvo.
  const [hospital, setHospital] = useState(
    documento ? d?.hospital || "" : HOSPITAL_PADRAO
  );
  const [medicoExecutante, setMedicoExecutante] = useState(
    d?.medicoExecutante || ""
  );
  const [observacoes, setObservacoes] = useState(d?.observacoes || "");

  const [salvando, setSalvando] = useState(false);
  const [salvoOk, setSalvoOk] = useState(false);
  const [erro, setErro] = useState("");
  const [docId, setDocId] = useState<string | null>(documento?.id || null);

  const podeGerar =
    agendamento.trim() !== "" && dataISO !== "" && setor.trim() !== "";

  // Firestore NÃO aceita `undefined` — montar o objeto SÓ com os campos
  // preenchidos (omitir hora/médico/observações quando vazios).
  const montarDados = (): DadosDeclaracaoAgendamento => {
    const dados: DadosDeclaracaoAgendamento = {
      agendamento: agendamento.trim(),
      data: isoParaBR(dataISO),
      setor: setor.trim(),
    };
    if (hora) dados.hora = horaParaBR(hora);
    if (hospital.trim()) dados.hospital = hospital.trim();
    if (medicoExecutante.trim()) dados.medicoExecutante = medicoExecutante.trim();
    if (observacoes.trim()) dados.observacoes = observacoes.trim();
    return dados;
  };

  // Profissional (enfermeira logada) + COREN para o cabeçalho/rodapé do PDF.
  const registroProfissional = usuarioLogado?.registroProfissional
    ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
    : "";
  const configPDF = () =>
    montarConfigDeclaracao(usuarioLogado?.nome || "", registroProfissional);

  // Cria (1ª vez) ou atualiza o mesmo documento. Retorna sucesso.
  const persistir = async (dados: DadosDeclaracaoAgendamento) => {
    if (!usuarioLogado) return false;
    const autor = { id: usuarioLogado.id || "", nome: usuarioLogado.nome || "" };
    const titulo = `Declaração de Agendamento - ${paciente.nome}`;
    try {
      if (docId) {
        await atualizarDocumentoEnfermagem({
          id: docId,
          pacienteId: paciente.id,
          titulo,
          dados,
          autor,
        });
      } else {
        const id = await criarDocumentoEnfermagem({
          pacienteId: paciente.id,
          tipo: "declaracao_agendamento",
          titulo,
          dados,
          autor,
          autorRegistro: registroProfissional,
        });
        setDocId(id);
      }
      setSalvoOk(true);
      if (onSalvo) onSalvo();
      return true;
    } catch (e) {
      console.error("Erro ao salvar declaração de enfermagem:", e);
      setErro("Não foi possível salvar. Tente novamente.");
      return false;
    }
  };

  const salvar = async () => {
    if (!podeGerar || salvando) return;
    setSalvando(true);
    setErro("");
    await persistir(montarDados());
    setSalvando(false);
  };

  const gerarPDF = async () => {
    if (!podeGerar || salvando) return;
    setSalvando(true);
    setErro("");
    const dados = montarDados();
    const ok = await persistir(dados);
    setSalvando(false);
    if (ok) gerarPDFDeclaracaoAgendamento(paciente, dados, configPDF());
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "90%",
          maxWidth: "640px",
          maxHeight: "90vh",
          backgroundColor: "white",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            backgroundColor: theme.primary[600],
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FileText size={18} /> Declaração de Agendamento
            </h2>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "13px",
                opacity: 0.9,
                fontFamily: theme.fonts.sans,
              }}
            >
              {paciente.nome}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "24px",
              cursor: "pointer",
              padding: "4px 8px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Corpo */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 24px",
            backgroundColor: "#f9fafb",
          }}
        >
          {/* Dados do paciente (automáticos) */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid " + theme.gray[200],
              borderRadius: "10px",
              padding: "12px 14px",
              marginBottom: "16px",
              fontSize: "13px",
              color: theme.gray[700],
            }}
          >
            <div>
              <strong>Paciente:</strong> {paciente.nome}
            </div>
            <div>
              <strong>CNS:</strong> {paciente.cns || "— (não cadastrado)"}
            </div>
            <div>
              <strong>Hospub:</strong> {paciente.hospub || "— (não cadastrado)"}
            </div>
          </div>

          <Campo label="Agendamento / procedimento *">
            <input
              type="text"
              value={agendamento}
              onChange={(e) => {
                setAgendamento(e.target.value);
                setSalvoOk(false);
              }}
              placeholder="Ex.: Biópsia Hepática guiada por Ultrassom"
              style={estiloInput}
            />
          </Campo>

          <div style={{ display: "flex", gap: "12px" }}>
            <Campo label="Data *" style={{ flex: 1 }}>
              <input
                type="date"
                value={dataISO}
                onChange={(e) => {
                  setDataISO(e.target.value);
                  setSalvoOk(false);
                }}
                style={estiloInput}
              />
            </Campo>
            <Campo label="Hora" style={{ width: "140px" }}>
              <input
                type="time"
                value={hora}
                onChange={(e) => {
                  setHora(e.target.value);
                  setSalvoOk(false);
                }}
                style={estiloInput}
              />
            </Campo>
          </div>

          <Campo label="Setor / local *">
            <input
              type="text"
              value={setor}
              onChange={(e) => {
                setSetor(e.target.value);
                setSalvoOk(false);
              }}
              placeholder="Ex.: Radiologia"
              style={estiloInput}
            />
          </Campo>

          <Campo label="Hospital / unidade">
            <input
              type="text"
              value={hospital}
              onChange={(e) => {
                setHospital(e.target.value);
                setSalvoOk(false);
              }}
              placeholder="Ex.: Hospital de Base Dr. Ary Pinheiro (ou outro, se fora do HB)"
              style={estiloInput}
            />
            <button
              type="button"
              onClick={() => {
                setHospital(HOSPITAL_PADRAO);
                setSalvoOk(false);
              }}
              title="Preencher com o hospital padrão"
              style={{
                marginTop: "6px",
                padding: "5px 12px",
                fontSize: "12px",
                backgroundColor: theme.gray[100],
                border: "1px solid " + theme.gray[200],
                borderRadius: "16px",
                cursor: "pointer",
                color: theme.gray[700],
                fontFamily: theme.fonts.sans,
              }}
            >
              🏥 {HOSPITAL_PADRAO}
            </button>
          </Campo>

          <Campo label="Médico executante (opcional)">
            <input
              type="text"
              value={medicoExecutante}
              onChange={(e) => {
                setMedicoExecutante(e.target.value);
                setSalvoOk(false);
              }}
              placeholder="Ex.: Dr. Elessandro Ferreira Dutra"
              style={estiloInput}
            />
          </Campo>

          <Campo label="Observações">
            <textarea
              value={observacoes}
              onChange={(e) => {
                setObservacoes(e.target.value);
                setSalvoOk(false);
              }}
              placeholder={
                "Uma por linha. Ex.:\n1. Coleta de exame laboratorial dia 29/06/2026.\n2. Internação dia 29/06/2026.\n3. Necessita de acompanhante."
              }
              rows={5}
              style={{ ...estiloInput, resize: "vertical", minHeight: "90px" }}
            />
          </Campo>

          {erro && (
            <div
              style={{
                marginTop: "8px",
                padding: "10px 14px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                color: "#b91c1c",
                fontSize: "13px",
              }}
            >
              {erro}
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid " + theme.gray[200],
            backgroundColor: "white",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {salvoOk && (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "12px",
                color: theme.primary[700],
                marginRight: "auto",
              }}
            >
              <Check size={14} /> Salvo no histórico
            </span>
          )}
          <button onClick={salvar} disabled={!podeGerar || salvando} style={botaoSecundario}>
            {salvando ? <Clock size={14} /> : <Check size={14} />} Salvar
          </button>
          <button
            onClick={gerarPDF}
            disabled={!podeGerar || salvando}
            style={{
              ...botaoSecundario,
              backgroundColor: podeGerar ? theme.primary[600] : theme.gray[300],
              color: "white",
              border: "none",
              cursor: podeGerar ? "pointer" : "not-allowed",
            }}
          >
            <Printer size={14} /> Gerar PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({
  label,
  children,
  style,
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ marginBottom: "12px", ...style }}>
      <label
        style={{
          display: "block",
          fontSize: "12px",
          fontWeight: 600,
          color: theme.gray[700],
          marginBottom: "4px",
          fontFamily: theme.fonts.sans,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const estiloInput: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: "8px",
  border: "1px solid " + theme.gray[300],
  fontFamily: theme.fonts.sans,
  fontSize: "14px",
  boxSizing: "border-box",
};

const botaoSecundario: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "9px 16px",
  fontSize: "13px",
  fontWeight: 600,
  backgroundColor: "white",
  border: "1px solid " + theme.gray[300],
  borderRadius: "10px",
  cursor: "pointer",
  color: theme.gray[700],
  fontFamily: theme.fonts.sans,
};
